// Kommersiellt innehåll för affärsområdet Foto — tjänstebeskrivning,
// prisspann och kundvillkor. ENDA sanningskälla; hårdkoda aldrig dessa
// siffror eller villkor på en sida, importera härifrån.
//
// Prisspannen är hämtade ur Bildindex 2026 (168 levererade uppdrag,
// januari 2024 – augusti 2026) och gäller exkl. moms. Regionala nivåer
// räknas om med indexvärdena i samma studie.

import {
  BILDINDEX,
  BILDINDEX_PATH,
  BILDINDEX_REGIONS,
} from "@/lib/data/bildindex-2026";

/** Tjänster vi faktiskt erbjuder — matchar `fotoServices` och navigationen. */
export interface FotoPriceRow {
  /** Nyckel mot Bildindex-raden. */
  indexService: string;
  label: string;
  /** Sidan som beskriver tjänsten i detalj. */
  path: string;
  /** Vad som ingår i ett normaluppdrag. */
  includes: string;
  priceMin: number;
  priceMax: number;
  onSite: string;
  leadMin: number;
  leadMax: number;
  delivered: string;
}

const row = (service: string) => {
  const r = BILDINDEX.find((b) => b.service === service);
  if (!r) throw new Error(`Bildindex saknar raden ${service}`);
  return r;
};

export const FOTO_PRICES: FotoPriceRow[] = [
  {
    indexService: "Företagsporträtt (team, studio eller på plats)",
    label: "Företagsporträtt och personalbilder",
    path: "/foto/foretagsportratt",
    includes:
      "Mobil studio eller vår studio, fotograf, ljussättning, urval och retusch av godkända bilder.",
    ...pick("Företagsporträtt (team, studio eller på plats)"),
  },
  {
    indexService: "Headshots enskild person",
    label: "Headshots, enskild person",
    path: "/foto/headshots",
    includes: "1–2 timmar, två ljussättningar, bakgrundsval och retuscherade slutbilder.",
    ...pick("Headshots enskild person"),
  },
  {
    indexService: "Produktfoto e-handel",
    label: "Produktfoto för e-handel och katalog",
    path: "/foto/produktfoto",
    includes:
      "Enhetlig ljussättning per serie, frilagd eller miljösatt bakgrund, export i webb- och tryckformat.",
    ...pick("Produktfoto e-handel"),
  },
  {
    indexService: "Kampanj- och livsstilsfoto",
    label: "Kampanj- och varumärkesfoto",
    path: "/foto/kampanjfoto",
    includes:
      "Bildmanus, platsval, ljussättning och färgsättning mot befintlig grafisk profil.",
    ...pick("Kampanj- och livsstilsfoto"),
  },
  {
    indexService: "Eventfoto / konferens",
    label: "Event, konferens och mässa",
    path: "/foto/eventfoto",
    includes:
      "Reportagebevakning, snabbleverans av webburval under dagen, full retusch efteråt.",
    ...pick("Eventfoto / konferens"),
  },
  {
    indexService: "Drönarfoto",
    label: "Drönarfoto",
    path: "/foto/dronarfoto",
    includes:
      "Flygning med certifierad pilot, kontroll av luftrum och tillstånd, stillbilder i hög upplösning.",
    ...pick("Drönarfoto"),
  },
  {
    indexService: "Foto + film samma dag",
    label: "Foto och film samma dag",
    path: "/foto/foto-och-film",
    includes:
      "Ett gemensamt inspelningstillfälle med fotograf och filmteam — stillbilder plus färdig film.",
    ...pick("Foto + film samma dag"),
  },
];

function pick(service: string) {
  const r = row(service);
  return {
    priceMin: r.priceMin,
    priceMax: r.priceMax,
    onSite: r.onSite,
    leadMin: r.leadMin,
    leadMax: r.leadMax,
    delivered: r.delivered,
  };
}

/** Regionalt index där riksmedian = 100. Returnerar 100 för okänd ort. */
export function fotoCityIndex(city: string): number {
  return BILDINDEX_REGIONS.find((r) => r.region === city)?.index ?? 100;
}

/** Ortsjusterat prisspann, avrundat till närmaste 500 kr. */
export function fotoCityPrice(row: FotoPriceRow, city?: string) {
  const idx = city ? fotoCityIndex(city) : 100;
  const round = (n: number) => Math.round((n * idx) / 100 / 500) * 500;
  return { min: round(row.priceMin), max: round(row.priceMax), index: idx };
}

export const FOTO_PRICE_SOURCE = {
  label: "Bildindex 2026",
  path: BILDINDEX_PATH,
  note:
    "Spannen är medianintervall ur Bildindex 2026 och gäller exkl. moms. Varje uppdrag offereras med fast pris innan bokning.",
} as const;

// ---- Tjänstebeskrivning -------------------------------------------------

export interface FotoServiceStep {
  n: number;
  title: string;
  body: string;
}

export const FOTO_PROCESS: FotoServiceStep[] = [
  {
    n: 1,
    title: "Förfrågan och offert",
    body: "Ni beskriver uppdrag, antal bilder och användning. Fenny återkommer inom en arbetsdag med upplägg, tidsplan och fast pris.",
  },
  {
    n: 2,
    title: "Planering",
    body: "Vi sätter schema, platser och eventuellt bildmanus. Vid porträttdagar bokar vi tidsluckor per person så att dagen håller.",
  },
  {
    n: 3,
    title: "Fotografering",
    body: "Studio eller mobil studio hos er — cirka 45 minuters riggning och tre gånger tre meter yta. Ljus, beskärning och bakgrund dokumenteras så att kompletteringar matchar.",
  },
  {
    n: 4,
    title: "Urval och retusch",
    body: "Ni får ett urval att godkänna. Godkända bilder retuscheras: hudton, färgsättning, bortplock av störande detaljer och format per kanal.",
  },
  {
    n: 5,
    title: "Leverans",
    body: "Nedladdningsbar mapp med högupplösta och webboptimerade filer, namngivna per person eller artikel. Vi arkiverar originalen i 24 månader.",
  },
];

// ---- Kundvillkor --------------------------------------------------------

export interface FotoTerm {
  id: string;
  heading: string;
  body: string;
}

export const FOTO_TERMS: FotoTerm[] = [
  {
    id: "pris",
    heading: "Fast pris, ingen bindning",
    body: "Varje uppdrag offereras med fast pris innan bokning. Ingen bindningstid och inga abonnemang — ni beställer per uppdrag. Tillkommande arbete utförs bara efter skriftligt godkännande.",
  },
  {
    id: "bokning",
    heading: "Bokning och avbokning",
    body: "Bokningen är bekräftad när ni godkänt offerten skriftligt. Avbokning senast fem arbetsdagar före fotodagen är kostnadsfri. Därefter debiteras 50 procent av arvodet, och vid avbokning samma dag hela arvodet samt eventuella tredjepartskostnader.",
  },
  {
    id: "leverans",
    heading: "Leverans",
    body: "Retuscherade bilder levereras inom det spann som anges per tjänst, räknat från godkänt urval. Vid event kan ett webburval levereras redan under dagen. Filerna levereras via nedladdningslänk och arkiveras hos oss i 24 månader.",
  },
  {
    id: "rattigheter",
    heading: "Användningsrätt",
    body: "Ni får fri, obegränsad användningsrätt till slutbilderna i egna kanaler: sajt, sociala medier, press, rekrytering och tryck. Köpt media, utomhusreklam och användning av inhyrda modeller regleras separat i offerten. Upphovsrätten stannar hos fotografen och vi ber om byline där det är möjligt.",
  },
  {
    id: "personuppgifter",
    heading: "Personuppgifter och samtycke",
    body: "Vid porträtt av medarbetare ansvarar ni som uppdragsgivare för samtycke till publicering. Vi lagrar bildfiler inom EU och raderar dem på begäran, med undantag för material som ingår i pågående leverans.",
  },
  {
    id: "betalning",
    heading: "Betalning",
    body: "Fakturering sker efter leverans med 20 dagars betalningsvillkor, om inget annat avtalats. Uppdrag över 50 000 kr faktureras till hälften vid bokning. Alla priser anges exklusive moms.",
  },
  {
    id: "avbrott",
    heading: "Väder, sjukdom och force majeure",
    body: "Utomhus- och drönarfoto kan flyttas kostnadsfritt vid väder som omöjliggör fotografering. Blir fotografen sjuk ersätter vi med likvärdig fotograf eller flyttar dagen utan kostnad för er.",
  },
];

/** Kort, citerbar sammanfattning för AEO/GEO. */
export const FOTO_TERMS_SUMMARY =
  "Fenny Foto arbetar med fast pris per uppdrag utan bindningstid. Avbokning är kostnadsfri till fem arbetsdagar före fotodagen, retuscherade bilder levereras inom 3–7 arbetsdagar från godkänt urval och kunden får fri användningsrätt i egna kanaler. Fakturering sker efter leverans med 20 dagars villkor, exklusive moms.";
